import React, { useState, useRef, useEffect } from "react";
import { ArrowUp, Sparkle } from "lucide-react";
import { GoogleDots } from "./GoogleBits";
import A4Sheet from "./A4Sheet";
import { interpretQuote } from "../lib/api";
import { usePlan } from "../context/PlanContext";
import { todayBR } from "../lib/format";
import { toast } from "sonner";

const PRESETS = [
  "Desenvolvimento Web / SaaS",
  "Design de Marca",
  "Consultoria",
  "Arquitetura",
  "Manutenção e Suporte",
];

const PLACEHOLDER =
  "Ex: Orçamento de site em Shopify para a cliente Maria Santos. Layout por R$ 3.500, configuração de pagamento por R$ 1.200 e suporte de 30 dias por R$ 800. Entrega em 20 dias.";

export default function CreateQuote({ profile, companyId, onComplete, initialPrompt, onConsumePrompt, userName, companyName }) {
  const { limits, usage, openUpgrade } = usePlan();
  const [text, setText] = useState("");
  const [messages, setMessages] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);
  const scrollRef = useRef(null);
  const taRef = useRef(null);

  const atQuoteLimit = () => {
    const q = limits?.quotes_per_month;
    return q != null && (usage?.quotes_this_month ?? 0) >= q;
  };

  useEffect(() => {
    if (taRef.current) {
      taRef.current.style.height = "auto";
      taRef.current.style.height = Math.min(taRef.current.scrollHeight, 200) + "px";
    }
  }, [text]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const run = async (nextMessages) => {
    setLoading(true);
    setQuestions([]);
    try {
      const res = await interpretQuote(nextMessages, profile?.company, companyId);
      if (res.status === "complete") {
        const data = {
          ...res.quote,
          issue_date: res.quote.issue_date || todayBR(),
        };
        onComplete(data);
      } else {
        const content = (res.message || "").trim() || "Pode me contar um pouco mais?";
        setQuestions(res.status === "needs_info" ? res.questions || [] : []);
        setMessages([...nextMessages, { role: "assistant", content }]);
      }
    } catch (e) {
      toast.error("Não foi possível interpretar agora. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const autoRunRef = useRef(false);
  useEffect(() => {
    if (initialPrompt && initialPrompt.trim() && !autoRunRef.current) {
      autoRunRef.current = true;
      if (atQuoteLimit()) { openUpgrade("quotes"); onConsumePrompt && onConsumePrompt(); return; }
      const next = [{ role: "user", content: initialPrompt.trim() }];
      setMessages(next);
      setStarted(true);
      run(next);
      onConsumePrompt && onConsumePrompt();
    }
  }, [initialPrompt]); // eslint-disable-line

  const submit = () => {
    const value = text.trim();
    if (!value || loading) return;
    if (!started && atQuoteLimit()) return openUpgrade("quotes");
    const next = [...messages, { role: "user", content: value }];
    setMessages(next);
    setText("");
    setStarted(true);
    run(next);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const firstName = (userName || "").trim().split(" ")[0];
  const empresa = companyName || profile?.company?.name || "sua empresa";
  const previewCompany = { ...(profile?.company || {}), logo: profile?.logo };
  const previewTheme = {
    primary: profile?.colors?.primary || "#0f172a",
    font: profile?.font || "sans",
    headerLabel: profile?.doc?.headerLabel || "Orçamento",
    logoRight: profile?.doc?.logoRight ?? false,
    logoSize: profile?.doc?.logoSize || "md",
  };
  const previewData = {
    client_name: "Cliente",
    title: "Proposta de Serviços",
    items: [
      { description: "Serviço principal", quantity: 1, unit_price: 2500 },
      { description: "Item adicional", quantity: 1, unit_price: 800 },
    ],
    issue_date: todayBR(),
  };

  return (
    <div className="min-h-screen pt-14 flex flex-col items-center px-5">
      {!started ? (
        <div className="flex-1 flex flex-col items-center justify-center w-full max-w-2xl -mt-6 py-10 float-up">
          <MiniPreview layout={profile?.layout || "minimal_classic"} company={previewCompany} theme={previewTheme} data={previewData} empresa={empresa} />
          <h1 className="mt-7 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 text-center leading-tight max-w-xl">
            {firstName ? `Olá, ${firstName}! ` : "Olá! "}Vamos criar um orçamento da <span className="google-text">{empresa}</span>
          </h1>
          <p className="text-slate-500 mt-3 text-center max-w-md text-[15px]">
            Fale comigo do seu jeito: tire dúvidas ou descreva o serviço e eu monto o orçamento.
          </p>
          {atQuoteLimit() && (
            <button
              onClick={() => openUpgrade("quotes")}
              data-testid="quote-limit-banner"
              className="mt-6 inline-flex items-center gap-2 text-[13px] font-medium text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-full px-4 py-2 hover:bg-amber-100 transition-colors"
            >
              <Sparkle size={14} /> Você atingiu seus orçamentos do mês — faça upgrade para continuar
            </button>
          )}
          <div className="w-full mt-8">
            <PromptBox
              text={text}
              setText={setText}
              onKey={onKey}
              submit={submit}
              loading={loading}
              taRef={taRef}
            />
          </div>
          <div className="flex flex-wrap justify-center gap-2 mt-5">
            {PRESETS.map((p) => (
              <button
                key={p}
                data-testid={`preset-${p}`}
                onClick={() => setText((t) => (t ? t : `Orçamento de ${p.toLowerCase()} para o cliente `))}
                className="text-[12px] text-slate-500 bg-white ring-1 ring-slate-200 hover:ring-slate-300 hover:text-slate-800 rounded-full px-3.5 py-1.5 transition-all"
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="w-full max-w-2xl flex-1 flex flex-col pt-8 pb-40">
          <div ref={scrollRef} className="flex-1 space-y-5 overflow-y-auto scrollbar-none">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} float-up`}>
                {m.role === "assistant" ? (
                  <div className="max-w-[85%] bg-white ring-1 ring-slate-200 rounded-2xl rounded-tl-md px-4 py-3">
                    <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-400 mb-1.5">
                      <span className="w-1.5 h-1.5 rounded-full google-gradient" /> Assistente Basiq
                    </div>
                    <div className="text-[14px] text-slate-700 whitespace-pre-line leading-relaxed" data-testid={`ai-question-${i}`}>
                      {m.content}
                    </div>
                  </div>
                ) : (
                  <div className="max-w-[85%] bg-slate-900 text-white rounded-2xl rounded-tr-md px-4 py-3 text-[14px] leading-relaxed whitespace-pre-line">
                    {m.content}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white ring-1 ring-slate-200 rounded-2xl px-4 py-3.5">
                  <GoogleDots />
                </div>
              </div>
            )}
          </div>
          <div className="fixed bottom-0 inset-x-0 px-5 pb-6 pt-10 bg-gradient-to-t from-[#fafafa] via-[#fafafa] to-transparent">
            <div className="max-w-2xl mx-auto">
              <PromptBox
                text={text}
                setText={setText}
                onKey={onKey}
                submit={submit}
                loading={loading}
                taRef={taRef}
                small
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const PromptBox = ({ text, setText, onKey, submit, loading, taRef, small }) => (
  <div className="relative bg-white rounded-3xl ring-1 ring-slate-200 shadow-[0_10px_40px_-15px_rgba(15,23,42,0.15)] focus-within:ring-slate-300 transition-all">
    <textarea
      ref={taRef}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={onKey}
      rows={small ? 1 : 3}
      placeholder={small ? "Responda aqui..." : PLACEHOLDER}
      data-testid="prompt-input"
      className="w-full resize-none bg-transparent outline-none px-5 py-4 pr-16 text-[15px] text-slate-800 placeholder:text-slate-400 leading-relaxed scrollbar-none"
    />
    <button
      onClick={submit}
      disabled={loading || !text.trim()}
      data-testid="prompt-submit"
      className="absolute bottom-3 right-3 grid place-items-center w-10 h-10 rounded-full bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
    >
      <ArrowUp size={18} strokeWidth={2.4} />
    </button>
  </div>
);

const MiniPreview = ({ layout, company, theme, data, empresa }) => {
  const W = 208;
  const scale = W / 794;
  return (
    <div className="flex flex-col items-center" data-testid="quote-model-preview">
      <div className="text-[11px] font-medium text-slate-400 mb-2 inline-flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full google-gradient" /> Seu modelo {empresa ? `· ${empresa}` : ""}
      </div>
      <div
        className="relative rounded-xl overflow-hidden ring-1 ring-slate-200 shadow-[0_16px_40px_-20px_rgba(15,23,42,0.28)]"
        style={{ width: W, height: 268, background: "#fff", transform: "rotate(-1deg)" }}
      >
        <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: 794, pointerEvents: "none" }}>
          <A4Sheet layout={layout} company={company} theme={theme} data={data} editable={false} />
        </div>
        <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 55%, rgba(250,250,250,0.85))" }} />
      </div>
    </div>
  );
};
